import { cn } from "lib/utils";
import type { ChangeEvent } from "react";
import { useId } from "react";

export type FromToNumberInputProps = {
  min: number;
  max: number;
  step?: number;
  fromValue?: number;
  toValue?: number;
  onFromChange?: (value: number) => void;
  onToChange?: (value: number) => void;
  unitName?: string;
  /** プルダウン候補。未指定なら min〜max を step 刻みで生成 */
  presets?: number[];
  testLabel?: string;
};

/**
 * 数値 from/to 入力リーフ。native <input type="number"> + <datalist> を使い
 * 「入力フォーム兼プルダウン」の挙動を実現する。
 * @param {FromToNumberInputProps} props props
 * @return {JSX.Element} from/to 入力
 */
export const FromToNumberInput = ({
  min,
  max,
  step = 1,
  fromValue,
  toValue,
  onFromChange,
  onToChange,
  unitName = "",
  presets,
  testLabel = "",
}: FromToNumberInputProps): JSX.Element => {
  const listId = useId();
  const effectivePresets =
    presets ??
    (() => {
      const out: number[] = [];
      for (let v = min; v <= max; v += step) out.push(v);
      return out;
    })();

  const handleFrom = (e: ChangeEvent<HTMLInputElement>) => {
    const n = Number(e.target.value);
    if (!Number.isNaN(n)) onFromChange?.(n);
  };
  const handleTo = (e: ChangeEvent<HTMLInputElement>) => {
    const n = Number(e.target.value);
    if (!Number.isNaN(n)) onToChange?.(n);
  };

  return (
    <div className="flex items-center gap-2">
      <input
        data-testid={testLabel ? `${testLabel}-from` : undefined}
        type="number"
        list={`${listId}-from`}
        className={cn(
          "input input-bordered input-sm w-24",
          "[&::-webkit-outer-spin-button]:appearance-none",
          "[&::-webkit-inner-spin-button]:appearance-none",
          "[appearance:textfield]",
        )}
        min={min}
        max={max}
        step={step}
        value={fromValue ?? ""}
        onChange={handleFrom}
      />
      <span className="text-base-content/60 shrink-0">〜</span>
      <input
        data-testid={testLabel ? `${testLabel}-to` : undefined}
        type="number"
        list={`${listId}-to`}
        className={cn(
          "input input-bordered input-sm w-24",
          "[&::-webkit-outer-spin-button]:appearance-none",
          "[&::-webkit-inner-spin-button]:appearance-none",
          "[appearance:textfield]",
        )}
        min={min}
        max={max}
        step={step}
        value={toValue ?? ""}
        onChange={handleTo}
      />
      {unitName && (
        <span className="text-xs text-base-content/70 shrink-0">
          {unitName}
        </span>
      )}
      <datalist id={`${listId}-from`}>
        {effectivePresets.map(v => (
          <option key={v} value={v} />
        ))}
      </datalist>
      <datalist id={`${listId}-to`}>
        {effectivePresets.map(v => (
          <option key={v} value={v} />
        ))}
      </datalist>
    </div>
  );
};
